import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, Star } from 'lucide-react';
import { timelineApi, TimelineEvent } from '../api/timeline.api';
import { useAuth } from '@contexts/AuthContext';
import PageInfoButton from '@/components/PageInfoButton';

const EVENT_TYPE_ICONS: Record<string, string> = {
  framework_assigned: '📋',
  evidence_uploaded: '📎',
  evidence_approved: '✅',
  evidence_rejected: '❌',
  certification_issued: '🏆',
  risk_identified: '⚠️',
  policy_approved: '📄',
  training_completed: '🎓',
  vendor_assessed: '🔍',
  gap_assessment: '📊',
  capa_raised: '🔧',
  capa_closed: '✔️',
  control_added: '🛡️',
  audit_started: '🔎',
  audit_completed: '🏁',
  default: '📌',
};

const EVENT_TYPES = [
  'framework_assigned', 'evidence_uploaded', 'evidence_approved',
  'certification_issued', 'risk_identified', 'policy_approved',
  'training_completed', 'vendor_assessed', 'gap_assessment',
  'capa_raised', 'capa_closed', 'control_added', 'audit_started', 'audit_completed',
];



function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const EMPTY_EVENT = {
  event_type: 'evidence_uploaded',
  event_title: '',
  event_description: '',
  entity_type: '',
  is_milestone: false,
};

const ComplianceTimeline: React.FC = () => {
  const { hasPermission } = useAuth();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [milestoneOnly, setMilestoneOnly] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_EVENT });
  const [submitting, setSubmitting] = useState(false);

  const canRecord = hasPermission('compliance.update');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await timelineApi.getEvents({
        ...(eventTypeFilter && { event_type: eventTypeFilter }),
        ...(milestoneOnly && { milestone_only: milestoneOnly }),
        ...(fromDate && { date_from: fromDate }),
        ...(toDate && { date_to: toDate }),
        limit: 50,
      });
      setEvents(res.events || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [eventTypeFilter, milestoneOnly, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const handleRecord = async () => {
    if (!form.event_type || !form.event_title.trim()) {
      alert('Event type and title are required');
      return;
    }
    setSubmitting(true);
    try {
      await timelineApi.createEvent({
        event_type: form.event_type,
        event_title: form.event_title,
        ...(form.event_description && { event_description: form.event_description }),
        ...(form.entity_type && { entity_type: form.entity_type }),
        is_milestone: form.is_milestone,
      });
      setCreateModal(false);
      setForm({ ...EMPTY_EVENT });
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Group events by date
  const grouped: Record<string, TimelineEvent[]> = {};
  events.forEach(e => {
    const day = new Date(e.event_at).toDateString();
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(e);
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Compliance Timeline</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-1">Chronological history of your compliance journey</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {canRecord && (
            <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Record Event
            </button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Event Types</option>
          {EVENT_TYPES.map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-600">
          <input type="checkbox" checked={milestoneOnly} onChange={e => setMilestoneOnly(e.target.checked)} />
          Milestones only
        </label>
        <span className="text-xs text-slate-400 ml-auto">{total} events</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-slate-600">Loading...</span>
        </div>
      ) : events.length === 0 ? (
        <div className="py-16 text-slate-400">
          <div className="text-4xl mb-3">📅</div>
          <p>No timeline events found.</p>
          {canRecord && <p className="text-sm mt-1">Click "Record Event" to add the first entry.</p>}
        </div>
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200" />

          <div className="space-y-6">
            {Object.entries(grouped).map(([day, dayEvents]) => (
              <div key={day}>
                {/* Date label */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 relative z-10" />
                  <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                    {new Date(day).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>

                {dayEvents.map(event => (
                  <div key={event.id} className="flex gap-4 mb-4 relative">
                    {/* Icon */}
                    <div className={`relative z-10 w-16 h-8 flex items-center justify-center flex-shrink-0 ${
                      event.is_milestone ? 'text-yellow-500' : 'text-slate-400'
                    }`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                        event.is_milestone
                          ? 'bg-yellow-100 border-2 border-yellow-400'
                          : 'bg-white border border-slate-200'
                      }`}>
                        {EVENT_TYPE_ICONS[event.event_type] || EVENT_TYPE_ICONS.default}
                      </div>
                    </div>

                    {/* Card */}
                    <div className={`flex-1 rounded-xl border p-4 ${
                      event.is_milestone ? 'border-yellow-200 bg-yellow-50' : 'bg-white'
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {event.is_milestone && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-400 flex-shrink-0" />
                          )}
                          <span className="font-semibold text-slate-900">{event.event_title}</span>
                          {event.entity_type && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{event.entity_type}</span>
                          )}
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full capitalize">
                            {event.event_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{formatTime(event.event_at)}</span>
                      </div>
                      {event.event_description && (
                        <p className="text-sm text-slate-600 mt-1.5">{event.event_description}</p>
                      )}
                      {event.performer_name && (
                        <p className="text-xs text-slate-400 mt-2">by {event.performer_name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Record Event Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-900">Record Timeline Event</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Event Type *</label>
                <select value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {EVENT_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input value={form.event_title} onChange={e => setForm(f => ({ ...f, event_title: e.target.value }))}
                  placeholder="Brief description of what happened..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Details</label>
                <textarea value={form.event_description} onChange={e => setForm(f => ({ ...f, event_description: e.target.value }))}
                  rows={3} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
                <input value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))}
                  placeholder="e.g. framework, policy, vendor..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_milestone} onChange={e => setForm(f => ({ ...f, is_milestone: e.target.checked }))} />
                <Star className="w-4 h-4 text-yellow-500" />
                <span>Mark as milestone</span>
              </label>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleRecord} disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Record Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceTimeline;
